import { Suspense, lazy } from 'react';
import type { XkEvent, XkEventGroup, XkStock } from '@/lib/api/types';
import { useFormPanel } from './formPanelStore';
import { ProjectForm } from '@/features/brand/ProjectForm';
import { XotskiForm } from '@/features/stock/XotskiForm';
import type { NewXotski } from '@/features/stock/AddXotskiDialog';
import { EventForm } from '@/features/events/EventForm';

// Lazy — pulls in the (heavy) SurveyPreview only when a preview is opened,
// keeping it out of the always-mounted layout chunk.
const StationGamePreview = lazy(() =>
  import('@/features/events/survey-config/StationGamePreview').then((m) => ({
    default: m.StationGamePreview,
  })),
);

/**
 * Renders the currently-active right-panel form (see `formPanelStore`). The
 * surrounding header/close chrome lives in `RightSidebar`; this component only
 * switches on `active.kind` and renders the matching form body, wiring its
 * success/cancel callbacks back to the store.
 *
 * When you migrate another dialog: widen `FormPanelKind`, extract its form
 * body (decoupled from the modal shell, like `ProjectForm`), and add a `case`.
 */
export function FormPanelHost() {
  const active = useFormPanel((s) => s.active);
  const closeForm = useFormPanel((s) => s.closeForm);

  if (!active) return null;

  // Cancel restores the rail; a successful save collapses it (the caller's
  // onDone usually redirects to the new entity first).
  const cancel = () => closeForm();
  const done = (result: unknown) => {
    active.onDone?.(result);
    closeForm({ collapse: true });
  };

  switch (active.kind) {
    case 'project': {
      const brandId = Number(active.context?.brandId);
      return (
        <ProjectForm
          brandId={brandId}
          onCancel={cancel}
          onCreated={(g: XkEventGroup) => done(g)}
        />
      );
    }
    case 'xotski': {
      const brandId = Number(active.context?.brandId);
      const stockId = active.context?.stockId
        ? Number(active.context.stockId)
        : undefined;
      const initial = (active.context?.initial as Partial<NewXotski> | undefined) ?? null;
      return (
        <XotskiForm
          brandId={brandId}
          stockId={stockId}
          initial={initial}
          onCancel={cancel}
          onSaved={(s: XkStock) => done(s)}
        />
      );
    }
    case 'event': {
      const brandId = Number(active.context?.brandId);
      const projectId = active.context?.projectId
        ? Number(active.context.projectId)
        : undefined;
      return (
        <EventForm
          brandId={brandId}
          projectId={projectId}
          onCancel={cancel}
          onCreated={(e: XkEvent) => done(e)}
        />
      );
    }
    case 'station-preview': {
      const eventId = Number(active.context?.eventId);
      const stationId = Number(active.context?.stationId);
      return (
        <Suspense fallback={<div className="flex-1 p-4 text-center text-xs text-[var(--color-xk-text-muted)]">…</div>}>
          <StationGamePreview eventId={eventId} stationId={stationId} />
        </Suspense>
      );
    }
    default:
      return null;
  }
}
